import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { FormModal } from "@/components/pawkt/FormModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PetsRepeater } from "@/components/pawkt/PetsRepeater";
import { Button } from "@/components/ui/button";
import { Plus, PawPrint, Search } from "lucide-react";
import { toast } from "sonner";
import { petHealthError } from "@/components/pawkt/VaccinationRecord";
import { petAgeError, petCurrentMonths, ageText } from "@/lib/petAge";
import { BreedSelect } from "@/components/pawkt/BreedSelect";

const SPECIES = ["Dog", "Cat", "Rabbit", "Bird", "Other"].map((s) => ({ value: s, label: s }));

export default function Pets() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [sp, setSp] = useSearchParams();
  const inCare = sp.get("in_care") === "1";

  const { data, isLoading, isError } = useQuery({ queryKey: ["pets", search, inCare], queryFn: async () => (await api.get(`/pets?search=${encodeURIComponent(search)}&limit=200${inCare ? "&in_care=1" : ""}`)).data });
  const { data: custs } = useQuery({ queryKey: ["customers-lite"], queryFn: async () => (await api.get(`/customers?limit=200`)).data });

  const custOptions = (custs?.items || []).map((c) => ({ value: c.id, label: `${c.name} · ${c.mobile || c.phone || ""}` }));

  return (
    <div>
      <PageHeader title="Pets" subtitle="Every pet's digital profile">
        <Button onClick={() => setOpen(true)} data-testid="add-pet-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"><Plus className="h-4 w-4" /> Add Pet</Button>
      </PageHeader>

      {inCare && (
        <div data-testid="pets-in-care-banner" className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span><b>Pets in care</b> · pets linked to an active customer{data ? ` · ${data.total.toLocaleString("en-IN")} pet${data.total === 1 ? "" : "s"}` : ""}{data && data.total > data.items.length ? ` (showing ${data.items.length})` : ""}</span>
          <button onClick={() => { sp.delete("in_care"); setSp(sp); }} data-testid="pets-in-care-clear" className="text-xs font-semibold underline">Show all pets</button>
        </div>
      )}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input data-testid="pet-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pet, breed or owner…"
          className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10" />
      </div>

      {isError ? <div className="text-sm text-rose-600" data-testid="pets-error">Couldn't load pets.</div> : isLoading ? <Loading /> : (data?.items?.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.items.map((p) => (
            <button key={p.id} onClick={() => navigate(`/pets/${p.id}`)} data-testid={`pet-${p.id}`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-emerald-300 hover:-translate-y-0.5 hover:shadow-md transition-all">
              <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center"><PawPrint className="h-6 w-6 text-emerald-600" /></div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-400 truncate">{[p.species, p.breed, petCurrentMonths(p) != null ? ageText(petCurrentMonths(p)) : "Age unknown"].filter(Boolean).join(" · ")}</div>
                <div className="text-xs text-slate-400 truncate">Owner: {p.owner_name}</div>
              </div>
              <div className="ml-auto text-right"><div className="text-xs text-slate-400">LTV</div><div className="text-sm font-semibold text-slate-800">{inr(p.lifetime_revenue)}</div></div>
            </button>
          ))}
        </div>
      ) : <Empty icon={PawPrint} title={search || inCare ? "No matching pets" : "No pets yet"} subtitle={search || inCare ? "Try a different search." : "Add a pet and link it to its owner."} />)}

      {open && <AddPetsModal custOptions={custOptions} onClose={() => setOpen(false)}
        onDone={() => { qc.invalidateQueries({ queryKey: ["pets"] }); setOpen(false); }} />}
    </div>
  );
}

function AddPetsModal({ custOptions, onClose, onDone }) {
  const [customerId, setCustomerId] = useState("");
  const [pets, setPets] = useState([{ name: "", species: "Dog", species_id: "dog", breed: "", gender: "" }]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!customerId) { toast.error("Select an owner"); return; }
    const clean = pets.filter((p) => (p.name || "").trim());
    if (!clean.length) { toast.error("Add at least one pet with a name"); return; }
    const he = clean.map((p) => petAgeError(p) || petHealthError(p)).find(Boolean);
    if (he) { toast.error(he); return; }
    setBusy(true);
    try {
      await api.post(`/customers/${customerId}/pets`, { pets: clean });
      toast.success(`${clean.length} pet${clean.length > 1 ? "s" : ""} added`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md max-h-[88vh] overflow-y-auto" data-testid="pet-add-modal">
        <DialogHeader><DialogTitle className="font-display">Add Pets</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Owner</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="bg-white" data-testid="pet-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
              <SelectContent className="bg-white max-h-60">{custOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <PetsRepeater value={pets} onChange={setPets} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="add-pets-submit" className="bg-slate-900 text-white">{busy ? "Adding…" : "Add Pets"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
